import { Suspense, useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { companyTranslations } from '../../locales/companyTranslations';
import { Canvas } from '@react-three/fiber';
import * as THREE from 'three';
import { OrbitControls, useGLTF } from '@react-three/drei';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 20px;
  gap: 30px;
  height: 130vh;
  position: relative;
`;

const Header = styled.div`
  width: 100%;
  text-align: center;
`;

const Headline = styled.h2`
  font-size: 2.5rem;
  font-weight: bold;
  margin: 0;
  color: white;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const CanvasContainer = styled.div`
  width: 800px;
  height: 450px;
  position: relative;
  border-radius: 30px;
  overflow: hidden;

  @media (max-width: 768px) {
    width: 100%;
    height: 90vw;
  }
`;

const Description = styled.div`
  max-width: 600px;
  font-size: 1rem;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.9);
  text-align: center;
  padding: 0 20px;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const InfoSection = styled.div`
  width: 100%;
  margin-top: 100px;
  max-width: 1000px;
  padding: 20px;
`;

const VisionMissionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 150px;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;

  @media (max-width: 768px) {
    gap: 40px;
  }
`;

const InfoCard = styled.div`
  background: rgba(255, 255, 255, 0);
  border-radius: 15px;
  padding: 30px;
  transition: transform 0.3s ease;
  text-align: center;

  &:hover {
    transform: translateY(-5px);
  }

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const InfoTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: bold;
  color: white;
  margin: 0 0 15px 0;

  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

const InfoContent = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const LoaderContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(5px);
  border-radius: 30px;
  opacity: ${props => props.$isLoading ? 1 : 0};
  visibility: ${props => props.$isLoading ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const LoaderSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  border-top-color: white;
  animation: ${spin} 1s linear infinite;
`;

function FaceModel({ onLoad }) {
  const { scene } = useGLTF('/face.glb', undefined, undefined, 
    (progress) => {
      console.log('Loading progress:', progress);
    },
    (error) => {
      console.error('Error loading model:', error);
    }
  );
  
  useEffect(() => {
    if (scene) {
      console.log('Scene loaded:', scene);
      // Center the model
      const box = new THREE.Box3().setFromObject(scene);
      const center = box.getCenter(new THREE.Vector3());
      scene.position.sub(center);
      
      // Scale the model if needed
      const size = box.getSize(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z);
      const scale = 1 / maxDim;
      scene.scale.multiplyScalar(scale);

      scene.traverse((child) => {
        if (child.isMesh) {
          console.log('Processing mesh:', child.name);
          child.material = new THREE.MeshBasicMaterial({
            color: 'white',
            wireframe: true
          });
        }
      });
      onLoad?.();
    }
  }, [scene, onLoad]);

  return scene ? <primitive object={scene} /> : null;
}

function CompanyTab({ currentLang }) {
  const { headline, description, vision, mission } = companyTranslations[currentLang];
  const [isLoading, setIsLoading] = useState(true);

  const handleModelLoad = () => {
    setIsLoading(false);
  };


  return (
    <Container>
      <Header>
        <Headline>{headline}</Headline>
      </Header>

      <CanvasContainer>
        <Canvas
          camera={{ position: [0, 0, 2], fov: 30 }}
          style={{ background: 'transparent' }}
        >
          <Suspense fallback={null}>
            <ambientLight intensity={0.5} />
            <hemisphereLight intensity={3} groundColor="#444444" />
            <directionalLight intensity={1.5} position={[1, 1, 1]} />
            <FaceModel onLoad={handleModelLoad} />
            <OrbitControls
              enableZoom={false}
              enablePan={false}
              enableDamping={true}
              dampingFactor={0.05}
              autoRotate={true}
              autoRotateSpeed={5}
            />
          </Suspense>
        </Canvas>
        <LoaderContainer $isLoading={isLoading}>
          <LoaderSpinner />
        </LoaderContainer>
      </CanvasContainer>

      <Description>
        <p>{description}</p>
      </Description>

      <InfoSection>
        <VisionMissionContainer>
          <InfoCard>
            <InfoTitle>{vision.title}</InfoTitle>
            <InfoContent>{vision.content}</InfoContent>
          </InfoCard>
          <InfoCard>
            <InfoTitle>{mission.title}</InfoTitle>
            <InfoContent>{mission.content}</InfoContent>
          </InfoCard>
        </VisionMissionContainer>
      </InfoSection>
    </Container>
  );
}

useGLTF.preload('/face.glb');


export default CompanyTab;